import React, { useEffect, useRef, useState } from 'react';

import CalendarIcon from 'components/icons/CalendarIcon';

import './CustomDatePicker.css';

interface CustomDatePickerProps {
  value: string; // YYYY-MM-DD format
  placeholder?: string;
  disabled?: boolean;
  isInvalid?: boolean;
  onChange: (value: string) => void;
}

function CustomDatePicker({
  value,
  placeholder = 'dd/mm/yyyy',
  disabled = false,
  isInvalid = false,
  onChange,
}: CustomDatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Convert YYYY-MM-DD to dd/mm/yyyy for display
  useEffect(() => {
    if (value) {
      const [year, month, day] = value.split('-');
      setInputValue(`${day}/${month}/${year}`);
      setViewMonth(parseInt(month, 10) - 1);
      setViewYear(parseInt(year, 10));
    } else {
      setInputValue('');
    }
  }, [value]);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);

    // Try to parse dd/mm/yyyy
    const match = val.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (match) {
      const [, day, month, year] = match;
      const isoDate = `${year}-${month}-${day}`;
      onChange(isoDate);
    }
  };

  const handleDateSelect = (day: number) => {
    const month = (viewMonth + 1).toString().padStart(2, '0');
    const dayStr = day.toString().padStart(2, '0');
    const isoDate = `${viewYear}-${month}-${dayStr}`;
    onChange(isoDate);
    setIsOpen(false);
  };

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(viewMonth, viewYear);
    const firstDay = getFirstDayOfMonth(viewMonth, viewYear);
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected =
        value &&
        value ===
          `${viewYear}-${(viewMonth + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

      days.push(
        <button
          key={day}
          type="button"
          className={`calendar-day ${isSelected ? 'selected' : ''}`}
          onClick={() => handleDateSelect(day)}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const years = Array.from(
    { length: 100 },
    (_, i) => new Date().getFullYear() - i
  );

  return (
    <div className="custom-datepicker-wrapper" ref={wrapperRef}>
      <div className="custom-datepicker-input-wrapper">
        <input
          type="text"
          className={`custom-datepicker-input ${isInvalid ? 'is-invalid' : ''}`}
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          onChange={handleInputChange}
          onFocus={() => !disabled && setIsOpen(true)}
        />
        <button
          type="button"
          className="custom-datepicker-icon"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
        >
          <CalendarIcon size={16} color="#6c757d" />
        </button>
      </div>

      {isOpen && (
        <div className="custom-datepicker-dropdown">
          <div className="calendar-header">
            <button
              type="button"
              className="calendar-nav"
              onClick={() => {
                if (viewMonth === 0) {
                  setViewMonth(11);
                  setViewYear(viewYear - 1);
                } else {
                  setViewMonth(viewMonth - 1);
                }
              }}
            >
              ‹
            </button>
            <div className="calendar-selects">
              <select
                value={viewMonth}
                onChange={e => setViewMonth(parseInt(e.target.value, 10))}
                className="calendar-select"
              >
                {months.map((month, index) => (
                  <option key={month} value={index}>
                    {month}
                  </option>
                ))}
              </select>
              <select
                value={viewYear}
                onChange={e => setViewYear(parseInt(e.target.value, 10))}
                className="calendar-select"
              >
                {years.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="calendar-nav"
              onClick={() => {
                if (viewMonth === 11) {
                  setViewMonth(0);
                  setViewYear(viewYear + 1);
                } else {
                  setViewMonth(viewMonth + 1);
                }
              }}
            >
              ›
            </button>
          </div>
          <div className="calendar-weekdays">
            <div className="calendar-weekday">Mo</div>
            <div className="calendar-weekday">Tu</div>
            <div className="calendar-weekday">We</div>
            <div className="calendar-weekday">Th</div>
            <div className="calendar-weekday">Fr</div>
            <div className="calendar-weekday">Sa</div>
            <div className="calendar-weekday">Su</div>
          </div>
          <div className="calendar-grid">{renderCalendar()}</div>
        </div>
      )}
    </div>
  );
}

export default CustomDatePicker;
